// src/features/settings/components/registration-codes-panel/RegistrationCodeCreateForm.jsx
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Collapse,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import {
  REGISTRATION_TYPES,
  WORK_TIME_TYPES,
  REGISTRATION_CATEGORIES,
  STATISTICAL_CATEGORIES,
  ACCOUNT_HANDLINGS,
  TIME_OFF_TYPES,
  LEAVE_TYPES,
  OBSCURED_CATEGORIES,
  PERIOD_TYPES,
  FUNDING_SOURCES,
} from '../../../tidsregistering/constants/registrationCodeEnums';
import { FORM_MAX_WIDTH, FormActions, FormRow, FormSection, FormStack } from '../../../../components/common/form';

const FLAG_FIELDS = [
  'is_registration_code', 'is_time_detail_code', 'is_wishable',
  'is_allowed_without_end_date', 'is_end_day_code',
  'is_allowed_to_delete_activities', 'requires_documentation',
];

// Select over one of the registration-code enums, labelled via
// settings:registration_codes.enums.<field>.<value> (falls back to the raw value).
function EnumSelect({ field, options, value, onChange, required = false, allowEmpty = false }) {
  const { t } = useTranslation(['settings']);
  const label = t(`settings:registration_codes.labels.${field}`);
  const labelId = useId();
  return (
    <FormControl fullWidth required={required}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select labelId={labelId}
        value={value ?? ''}
        label={label}
        onChange={(e) => onChange(field, allowEmpty ? (e.target.value || null) : e.target.value)}
      >
        {allowEmpty && <MenuItem value=""><em>—</em></MenuItem>}
        {options.map((v) => (
          <MenuItem key={v} value={v}>
            {t(`settings:registration_codes.enums.${field}.${v}`, { defaultValue: v })}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default function RegistrationCodeCreateForm({
  visible, values, errors, loading, reasonCodeTypes, onChange, onSubmit, onCancel,
}) {
  const { t } = useTranslation(['settings', 'common']);

  return (
    <Collapse in={visible}>
      <Box sx={{ maxWidth: FORM_MAX_WIDTH, display: 'flex', flexDirection: 'column', gap: 5, mb: 5 }}>
        <FormSection
          title={t('settings:registration_codes.form.create_title')}
          description={t('settings:registration_codes.form.section_basic')}
        >
          <FormRow widths={[1, 3]} minWidth={140}>
            <TextField
              label={t('settings:registration_codes.labels.code')}
              value={values.code}
              onChange={(e) => onChange('code', e.target.value)}
              required
              fullWidth
              error={Boolean(errors.code)}
              helperText={errors.code}
              inputProps={{ maxLength: 20 }}
            />
            <TextField
              label={t('settings:registration_codes.labels.title')}
              value={values.title}
              onChange={(e) => onChange('title', e.target.value)}
              required
              fullWidth
              error={Boolean(errors.title)}
              helperText={errors.title}
              inputProps={{ maxLength: 255 }}
            />
          </FormRow>
          <TextField
            label={t('settings:registration_codes.labels.description')}
            value={values.description}
            onChange={(e) => onChange('description', e.target.value)}
            fullWidth
            multiline
            minRows={2}
            inputProps={{ maxLength: 1000 }}
          />
        </FormSection>

        <FormSection title={t('settings:registration_codes.form.section_classification')}>
          <FormControl fullWidth required error={Boolean(errors.reason_code_type_id)}>
            <InputLabel id="registration-code-create-form-reason-code-type-label">{t('settings:registration_codes.labels.reason_code_type')}</InputLabel>
            <Select labelId="registration-code-create-form-reason-code-type-label"
              value={values.reason_code_type_id}
              label={t('settings:registration_codes.labels.reason_code_type')}
              onChange={(e) => onChange('reason_code_type_id', e.target.value)}
            >
              {reasonCodeTypes.map((rct) => (
                <MenuItem key={rct.reason_code_type_id} value={rct.reason_code_type_id}>
                  {rct.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormRow>
            <EnumSelect field="registration_type" options={REGISTRATION_TYPES} value={values.registration_type} onChange={onChange} required />
            <EnumSelect field="work_time_type" options={WORK_TIME_TYPES} value={values.work_time_type} onChange={onChange} required />
          </FormRow>
          <FormRow>
            <EnumSelect field="registration_category" options={REGISTRATION_CATEGORIES} value={values.registration_category} onChange={onChange} required />
            <EnumSelect field="statistical_category" options={STATISTICAL_CATEGORIES} value={values.statistical_category} onChange={onChange} required />
          </FormRow>
          <FormRow>
            <EnumSelect field="account_handling" options={ACCOUNT_HANDLINGS} value={values.account_handling} onChange={onChange} required />
            <EnumSelect field="time_off_type" options={TIME_OFF_TYPES} value={values.time_off_type} onChange={onChange} />
          </FormRow>
          <FormRow>
            <EnumSelect field="leave_type" options={LEAVE_TYPES} value={values.leave_type} onChange={onChange} />
            <EnumSelect field="obscured_category" options={OBSCURED_CATEGORIES} value={values.obscured_category} onChange={onChange} allowEmpty />
          </FormRow>
          <EnumSelect field="funding_source" options={FUNDING_SOURCES} value={values.funding_source} onChange={onChange} allowEmpty />
        </FormSection>

        <FormSection title={t('settings:registration_codes.form.section_flags')}>
          <FormStack gap={1}>
            {FLAG_FIELDS.map((field) => (
              <FormControlLabel
                key={field}
                control={
                  <Switch
                    checked={Boolean(values[field])}
                    onChange={(e) => onChange(field, e.target.checked)}
                  />
                }
                label={<Typography variant="body2">{t(`settings:registration_codes.labels.${field}`)}</Typography>}
              />
            ))}
          </FormStack>
        </FormSection>

        <FormSection title={t('settings:registration_codes.form.section_policy')}>
          <FormRow>
            <TextField
              label={t('settings:registration_codes.labels.required_seniority_months')}
              type="number"
              fullWidth
              value={values.required_seniority_months}
              onChange={(e) => onChange('required_seniority_months', Number(e.target.value))}
              inputProps={{ min: 0 }}
            />
            <TextField
              label={t('settings:registration_codes.labels.max_per_period')}
              type="number"
              fullWidth
              value={values.max_per_period ?? ''}
              onChange={(e) => onChange('max_per_period', e.target.value === '' ? null : Number(e.target.value))}
              inputProps={{ min: 0 }}
            />
          </FormRow>
          <EnumSelect field="period_type" options={PERIOD_TYPES} value={values.period_type} onChange={onChange} allowEmpty />
        </FormSection>

        <FormActions
          onCancel={onCancel}
          onSubmit={onSubmit}
          submitLabel={t('common:buttons.create')}
          submitIcon={<SaveIcon />}
          loading={loading}
        />
      </Box>
    </Collapse>
  );
}
